/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as Teams from '@microsoft/teams-js';
import * as React from 'react';
import {
    currentScopeInCreationVar,
    currentScopeInListVar,
    currentTeamInCreationVar,
    currentTemplateVar,
    modalRefVar,
    TeamBasicInfo,
    TemplateScope,
} from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../../../logger';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { useIsAdmin } from '../../../../../utilities/TemplateManagementUtils';
import { PreSetupTemplateStepProps, PreSetupTemplateSteps } from '../PreSetupTemplateSteps';
import { TeamOption, ViewTeamComponent, ViewTeamComponentProps } from './ViewTeam.component';

export const ViewTeamContainer: React.FC<PreSetupTemplateStepProps> = (
    props: PreSetupTemplateStepProps
): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const isPreviewTemplateEnabled = host.getEcsConfig().MicrosoftTeamApprovals.TemplatePreviewEnabled;
    const [teamOptions, setTeamOptions] = React.useState<TeamOption[]>([]);

    const { navigateTo, done } = props;

    const selectedTeam = useReactiveVar<TeamBasicInfo>(currentTeamInCreationVar);
    const scopeInList = useReactiveVar(currentScopeInListVar);
    const currentTemplate = useReactiveVar(currentTemplateVar);
    const isAdmin = useIsAdmin();

    React.useEffect((): void => {
        host.getUserOwnedTeams().then((teamList: Teams.TeamInformation[]): void => {
            const options = teamList?.map((team: Teams.TeamInformation): TeamOption => {
                return {
                    id: team.groupId,
                    name: team.teamName,
                    pictureUrl: team.thumbnailUri,
                };
            });

            setTeamOptions(options);
        });
    }, [host, setTeamOptions]);

    // By default try to select the team as same as the team scope in list page
    React.useEffect((): void => {
        if (!selectedTeam) {
            currentTeamInCreationVar(scopeInList.team && { ...scopeInList.team });
        }
        modalRefVar()?.current?.focus();
    }, []);

    const onTeamSelected = (id: string, name: string): void => {
        logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.SelectTeam, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.SelectTeam,
            'click',
            'nav',
            'templateInstanceTeam',
            'templateInstanceTeamButton',
            PanelRegion.Modal,
            PanelType.PreSetupTemplateInstance
        );

        currentTeamInCreationVar({ id, name });
    };

    const onNext = (): void => {
        currentScopeInCreationVar(TemplateScope.Team);
        done();
    };

    const onBack = async (): Promise<void> => {
        logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.CarouselPreviousClicked, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.CarouselPreviousClicked,
            'click',
            'nav',
            'ActionButton',
            'carouselButton',
            PanelRegion.Modal,
            PanelType.PreSetupTemplateInstance
        );

        if (isAdmin) {
            navigateTo(PreSetupTemplateSteps.SelectScope);
        } else if (currentTemplate) {
            if (isPreviewTemplateEnabled) {
                navigateTo(PreSetupTemplateSteps.PreviewTemplate);
            } else {
                navigateTo(PreSetupTemplateSteps.BrowseTemplates);
            }
        } else {
            navigateTo(PreSetupTemplateSteps.BrowseTemplates);
        }
    };

    const componentProps: ViewTeamComponentProps = {
        selectedTeamId: selectedTeam?.id,
        onTeamSelected,
        teamOptions,
        onNext,
        onBack,
    };

    return <ViewTeamComponent {...componentProps} />;
};
